import { create } from 'zustand';
import { createId, createLayer, createProject, defaultTransform } from '../core/project';
import type { Asset, Effect, Keyframe, Layer, Project, SceneObject } from '../core/schema';
import { rippleDelete, splitLayer } from '../core/timeline';
import { ingestFile } from '../services/mediaRegistry';
import { createDebouncedProjectSaver } from '../services/persistence';
import { usePlaybackStore } from './playbackStore';

export type Workspace = 'video' | 'motion' | '3d' | 'audio';
export type ModalName = 'command' | 'export' | 'new-project' | 'projects' | 'settings' | 'shortcuts' | 'effects' | 'captions' | null;

export interface Toast { id: string; message: string; tone?: 'default' | 'success' | 'error' }

interface EditorState {
  project: Project;
  ready: boolean;
  workspace: Workspace;
  leftPanel: 'media' | 'layers' | 'effects' | 'text' | 'templates';
  inspectorTab: 'properties' | 'effects' | 'animation';
  selectedLayerIds: string[];
  selectedAssetId: string | null;
  snapping: boolean;
  timelineZoom: number;
  timelineScroll: number;
  leftPanelWidth: number;
  inspectorWidth: number;
  timelineHeight: number;
  modal: ModalName;
  diagnostics: boolean;
  compactTimeline: boolean;
  mobilePanelOpen: boolean;
  toasts: Toast[];
  past: Project[];
  future: Project[];
  transactionBase: Project | null;

  replaceProject(project: Project): void;
  setReady(ready: boolean): void;
  setWorkspace(workspace: Workspace): void;
  setLeftPanel(panel: EditorState['leftPanel']): void;
  setMobilePanelOpen(open: boolean): void;
  setInspectorTab(tab: EditorState['inspectorTab']): void;
  setModal(modal: ModalName): void;
  setTimelineZoom(zoom: number): void;
  setTimelineScroll(scroll: number): void;
  setPanelSize(panel: 'left' | 'inspector' | 'timeline', size: number): void;
  toggleSnapping(): void;
  toggleDiagnostics(): void;
  selectLayer(id: string | null, additive?: boolean): void;
  selectAsset(id: string | null): void;
  updateLayer(id: string, update: Partial<Layer> | ((layer: Layer) => void), history?: boolean): void;
  updateProject(recipe: (project: Project) => void, history?: boolean): void;
  addLayer(type: Layer['type'], start?: number): string;
  addAssetToTimeline(assetId: string, start?: number): string | null;
  importFiles(files: File[], placeOnTimeline?: boolean): Promise<void>;
  removeSelection(ripple?: boolean): void;
  splitSelection(): void;
  duplicateSelection(): void;
  precomposeSelection(): void;
  openComposition(id: string): void;
  reorderLayer(id: string, toIndex: number): void;
  addMarker(): void;
  addEffect(layerId: string, effect: Effect): void;
  addKeyframe(layerId: string, property: string, value: Keyframe['value']): void;
  beginTransaction(): void;
  endTransaction(): void;
  undo(): void;
  redo(): void;
  showToast(message: string, tone?: Toast['tone']): void;
  dismissToast(id: string): void;
}

const saver = createDebouncedProjectSaver();
const initialProject = createProject();

function commitProject(state: EditorState, next: Project, history: boolean) {
  next.updatedAt = Date.now();
  saver.schedule(next);
  return {
    project: next,
    ...(history ? { past: [...state.past.slice(-49), structuredClone(state.project)], future: [] } : {})
  };
}

function defaultSceneObjects(): SceneObject[] {
  return [
    { id: createId('obj'), name: 'Key Light', type: 'light', parentId: null, visible: true, locked: false, transform: defaultTransform([3, 4, 3]), material: { color: '#ffffff', metallic: 0, roughness: 1, emission: '#000000', opacity: 1 } },
    { id: createId('obj'), name: 'Open Motion Cube', type: 'cube', parentId: null, visible: true, locked: false, transform: { ...defaultTransform([0, 0, 0]), rotation: [16, -24, 0] }, material: { color: '#e6ff55', metallic: 0.25, roughness: 0.4, emission: '#101200', opacity: 1 } },
    { id: createId('obj'), name: 'Sphere', type: 'sphere', parentId: null, visible: true, locked: false, transform: { ...defaultTransform([-2.2, -0.35, -0.7]), scale: [0.65, 0.65, 0.65] }, material: { color: '#8f9fff', metallic: 0.55, roughness: 0.25, emission: '#000000', opacity: 1 } }
  ];
}

export const useEditorStore = create<EditorState>((set, get) => ({
  project: initialProject,
  ready: false,
  workspace: 'video',
  leftPanel: 'media',
  inspectorTab: 'properties',
  selectedLayerIds: [initialProject.compositions[initialProject.activeCompositionId].layerIds[1]],
  selectedAssetId: null,
  snapping: true,
  timelineZoom: 88,
  timelineScroll: 0,
  leftPanelWidth: 284,
  inspectorWidth: 308,
  timelineHeight: 292,
  modal: null,
  diagnostics: false,
  compactTimeline: false,
  mobilePanelOpen: false,
  toasts: [],
  past: [],
  future: [],
  transactionBase: null,

  replaceProject: (project) => {
    usePlaybackStore.getState().setTime(Math.min(2, project.compositions[project.activeCompositionId].duration));
    set({ project, selectedLayerIds: [], selectedAssetId: null, past: [], future: [], transactionBase: null, ready: true });
    saver.schedule(project);
  },
  setReady: (ready) => set({ ready }),
  setWorkspace: (workspace) => set({ workspace, leftPanel: workspace === '3d' ? 'layers' : workspace === 'motion' ? 'effects' : 'media', mobilePanelOpen: true }),
  setLeftPanel: (leftPanel) => set({ leftPanel, mobilePanelOpen: true }),
  setMobilePanelOpen: (mobilePanelOpen) => set({ mobilePanelOpen }),
  setInspectorTab: (inspectorTab) => set({ inspectorTab }),
  setModal: (modal) => set({ modal }),
  setTimelineZoom: (timelineZoom) => set({ timelineZoom: Math.max(24, Math.min(360, timelineZoom)) }),
  setTimelineScroll: (timelineScroll) => set({ timelineScroll }),
  setPanelSize: (panel, size) => set(panel === 'left' ? { leftPanelWidth: Math.max(220, Math.min(460, size)) } : panel === 'inspector' ? { inspectorWidth: Math.max(260, Math.min(460, size)) } : { timelineHeight: Math.max(190, Math.min(window.innerHeight * 0.66, size)) }),
  toggleSnapping: () => set((state) => ({ snapping: !state.snapping })),
  toggleDiagnostics: () => set((state) => ({ diagnostics: !state.diagnostics })),
  selectLayer: (id, additive = false) => set((state) => {
    if (!id) return { selectedLayerIds: [] };
    if (!additive) return { selectedLayerIds: [id], selectedAssetId: null };
    return { selectedLayerIds: state.selectedLayerIds.includes(id) ? state.selectedLayerIds.filter((value) => value !== id) : [...state.selectedLayerIds, id], selectedAssetId: null };
  }),
  selectAsset: (selectedAssetId) => set({ selectedAssetId, selectedLayerIds: [] }),
  updateProject: (recipe, history = true) => set((state) => {
    const next = structuredClone(state.project);
    recipe(next);
    return commitProject(state, next, history && !state.transactionBase);
  }),
  updateLayer: (id, update, history = true) => get().updateProject((project) => {
    const layer = project.layers[id];
    if (!layer) return;
    if (typeof update === 'function') update(layer);
    else Object.assign(layer, update);
  }, history),
  addLayer: (type, start = usePlaybackStore.getState().currentTime) => {
    let newId = '';
    get().updateProject((project) => {
      const names: Record<Layer['type'], string> = { video: 'Video', audio: 'Audio', image: 'Image', text: 'Text', shape: 'Shape', caption: 'Caption', scene3d: '3D Scene', adjustment: 'Adjustment', null: 'Controller', group: 'Group' };
      const layer = createLayer(project, {
        name: names[type], type, start,
        duration: type === 'adjustment' ? 10 : 5,
        ...(type === 'text' ? { text: 'Your title', fontSize: 96, fontFamily: 'Inter, sans-serif', fontWeight: 700, color: '#ffffff' } : {}),
        ...(type === 'caption' ? { text: 'Edit caption text', fontSize: 46, fontFamily: 'Inter, sans-serif', fontWeight: 600, color: '#ffffff', transform: defaultTransform([0, 380, 0]) } : {}),
        ...(type === 'shape' ? { color: '#e6ff55', transform: { ...defaultTransform(), scale: [0.32, 0.32, 1] } } : {}),
        ...(type === 'scene3d' ? { duration: 10, scene: { objects: defaultSceneObjects(), activeObjectId: null, background: '#20242c', cameraPosition: [4, 3, 6], cameraTarget: [0, 0, 0] } } : {})
      });
      newId = layer.id;
      project.layers[layer.id] = layer;
      project.compositions[project.activeCompositionId].layerIds.unshift(layer.id);
    });
    set({ selectedLayerIds: [newId] });
    if (type === 'scene3d') set({ workspace: '3d', leftPanel: 'layers' });
    return newId;
  },
  addAssetToTimeline: (assetId, start = usePlaybackStore.getState().currentTime) => {
    const asset = get().project.assets[assetId];
    if (!asset || !['video', 'audio', 'image'].includes(asset.kind)) return null;
    let id = '';
    get().updateProject((project) => {
      const type = asset.kind as 'video' | 'audio' | 'image';
      const composition = project.compositions[project.activeCompositionId];
      const duration = Math.max(1 / composition.fps, Math.min(asset.duration ?? (type === 'image' ? 5 : 10), composition.duration - Math.min(start, composition.duration - 1 / composition.fps)));
      const sourceWidth = asset.width ?? composition.width;
      const sourceHeight = asset.height ?? composition.height;
      const fit = Math.min(composition.width / sourceWidth, composition.height / sourceHeight);
      const layer = createLayer(project, { name: asset.name.replace(/\.[^.]+$/, ''), type, assetId, start: Math.min(start, composition.duration - duration), duration, transform: { ...defaultTransform(), scale: [fit, fit, 1] } });
      id = layer.id;
      project.layers[id] = layer;
      composition.layerIds.unshift(id);
    });
    set({ selectedLayerIds: [id], selectedAssetId: null });
    return id;
  },
  importFiles: async (files, placeOnTimeline = true) => {
    const supported = files.filter((file) => file.size > 0);
    if (!supported.length) return;
    get().showToast(`Importing ${supported.length} file${supported.length === 1 ? '' : 's'}…`);
    const projectId = get().project.id;
    let cursor = usePlaybackStore.getState().currentTime;
    for (const file of supported) {
      try {
        const asset = await ingestFile(projectId, file);
        const duplicate = Object.values(get().project.assets).find((item) => item.hash && item.hash === asset.hash && item.size === asset.size);
        if (duplicate) { get().showToast(`${file.name} is already in this project.`); continue; }
        get().updateProject((project) => { project.assets[asset.id] = asset; });
        if (placeOnTimeline && ['video', 'audio', 'image'].includes(asset.kind)) {
          get().addAssetToTimeline(asset.id, cursor);
          cursor += Math.min(asset.duration ?? 5, 10);
        }
      } catch (error) { get().showToast(error instanceof Error ? error.message : `Could not import ${file.name}`, 'error'); }
    }
    get().showToast('Media import complete', 'success');
  },
  removeSelection: (ripple = false) => {
    const ids = get().selectedLayerIds;
    if (!ids.length) return;
    if (ripple) {
      set((state) => commitProject(state, rippleDelete(state.project, state.project.activeCompositionId, ids), true));
    } else {
      get().updateProject((project) => {
        const composition = project.compositions[project.activeCompositionId];
        for (const id of ids) delete project.layers[id];
        composition.layerIds = composition.layerIds.filter((id) => !ids.includes(id));
      });
    }
    set({ selectedLayerIds: [] });
  },
  splitSelection: () => {
    const time = usePlaybackStore.getState().currentTime;
    const ids = get().selectedLayerIds;
    const rightIds: string[] = [];
    get().updateProject((project) => {
      const composition = project.compositions[project.activeCompositionId];
      for (const id of ids) {
        const layer = project.layers[id];
        if (!layer || layer.locked) continue;
        const split = splitLayer(layer, time);
        if (!split) continue;
        const [left, right] = split;
        project.layers[id] = left;
        project.layers[right.id] = right;
        const index = composition.layerIds.indexOf(id);
        composition.layerIds.splice(index, 0, right.id);
        rightIds.push(right.id);
      }
    });
    if (rightIds.length) set({ selectedLayerIds: rightIds });
  },
  duplicateSelection: () => {
    const ids: string[] = [];
    get().updateProject((project) => {
      const composition = project.compositions[project.activeCompositionId];
      for (const sourceId of get().selectedLayerIds) {
        const source = project.layers[sourceId];
        if (!source) continue;
        const copy = structuredClone(source);
        copy.id = createId('layer');
        copy.name = `${source.name} copy`;
        copy.start = Math.min(composition.duration - copy.duration, copy.start + 0.2);
        project.layers[copy.id] = copy;
        composition.layerIds.splice(Math.max(0, composition.layerIds.indexOf(sourceId)), 0, copy.id);
        ids.push(copy.id);
      }
    });
    set({ selectedLayerIds: ids });
  },
  precomposeSelection: () => {
    const selected = get().selectedLayerIds;
    if (!selected.length) return;
    let groupId = '';
    get().updateProject((project) => {
      const parent = project.compositions[project.activeCompositionId];
      const layers = selected.map((id) => project.layers[id]).filter(Boolean);
      if (!layers.length) return;
      const start = Math.min(...layers.map((layer) => layer.start));
      const end = Math.max(...layers.map((layer) => layer.start + layer.duration));
      const childId = createId('comp');
      project.compositions[childId] = {
        id: childId,
        name: layers.length === 1 ? `${layers[0].name} Comp` : 'Pre-composition',
        width: parent.width,
        height: parent.height,
        fps: parent.fps,
        duration: end - start,
        backgroundColor: 'rgba(0,0,0,0)',
        layerIds: [...selected],
        markers: [],
        parentCompositionId: parent.id
      };
      for (const layer of layers) {
        layer.compositionId = childId;
        layer.start -= start;
      }
      const sourceIndexes = selected.map((id) => parent.layerIds.indexOf(id)).filter((index) => index >= 0);
      const firstIndex = sourceIndexes.length ? Math.min(...sourceIndexes) : 0;
      parent.layerIds = parent.layerIds.filter((id) => !selected.includes(id));
      const group = createLayer(project, {
        name: project.compositions[childId].name,
        type: 'group',
        compositionId: parent.id,
        nestedCompositionId: childId,
        start,
        duration: end - start
      });
      groupId = group.id;
      project.layers[group.id] = group;
      parent.layerIds.splice(firstIndex, 0, group.id);
    });
    if (groupId) set({ selectedLayerIds: [groupId] });
  },
  openComposition: (id) => {
    if (!get().project.compositions[id]) return;
    get().updateProject((project) => { project.activeCompositionId = id; }, false);
    usePlaybackStore.getState().setTime(0);
    set({ selectedLayerIds: [] });
  },
  reorderLayer: (id, toIndex) => get().updateProject((project) => {
    const ids = project.compositions[project.activeCompositionId].layerIds;
    const from = ids.indexOf(id);
    if (from < 0) return;
    ids.splice(from, 1);
    ids.splice(Math.max(0, Math.min(ids.length, toIndex)), 0, id);
  }),
  addMarker: () => get().updateProject((project) => {
    project.compositions[project.activeCompositionId].markers.push({ id: createId('marker'), time: usePlaybackStore.getState().currentTime, label: 'Marker', color: '#e6ff55' });
  }),
  addEffect: (layerId, effect) => get().updateLayer(layerId, (layer) => { layer.effects.push(effect); }),
  addKeyframe: (layerId, property, value) => get().updateLayer(layerId, (layer) => {
    const time = Math.max(0, usePlaybackStore.getState().currentTime - layer.start);
    const frames = layer.keyframes[property] ?? [];
    const existing = frames.find((frame) => Math.abs(frame.time - time) < 0.0001);
    if (existing) existing.value = value;
    else frames.push({ id: createId('kf'), time, value, interpolation: 'ease-in-out' });
    layer.keyframes[property] = frames.sort((a, b) => a.time - b.time);
  }),
  beginTransaction: () => set((state) => state.transactionBase ? {} : { transactionBase: structuredClone(state.project) }),
  endTransaction: () => set((state) => {
    if (!state.transactionBase) return {};
    saver.schedule(state.project);
    return { past: [...state.past.slice(-49), state.transactionBase], future: [], transactionBase: null };
  }),
  undo: () => set((state) => {
    const previous = state.past.at(-1);
    if (!previous) return {};
    saver.schedule(previous);
    return { project: previous, past: state.past.slice(0, -1), future: [structuredClone(state.project), ...state.future].slice(0, 50), selectedLayerIds: [] };
  }),
  redo: () => set((state) => {
    const next = state.future[0];
    if (!next) return {};
    saver.schedule(next);
    return { project: next, past: [...state.past, structuredClone(state.project)].slice(-50), future: state.future.slice(1), selectedLayerIds: [] };
  }),
  showToast: (message, tone = 'default') => {
    const id = createId('toast');
    set((state) => ({ toasts: [...state.toasts, { id, message, tone }].slice(-4) }));
    setTimeout(() => get().dismissToast(id), 3500);
  },
  dismissToast: (id) => set((state) => ({ toasts: state.toasts.filter((toast) => toast.id !== id) }))
}));
